import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Collapse,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  FormHelperText,
  Heading,
  HStack,
  Input,
  Link,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiPlus, FiTrash2 } from 'react-icons/fi';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { PublicAPI, extractApiError } from '../services/api';

const MotionBox = motion(Box);

const INDUSTRIES = [
  { value: '', label: 'Select industry…' },
  { value: 'hvac_plumbing', label: 'HVAC / Plumbing' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'salon_spa', label: 'Salon / Spa' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'medical_dental', label: 'Medical / Dental' },
  { value: 'legal', label: 'Legal' },
  { value: 'other', label: 'Other' },
];

const REFERRALS = [
  { value: '', label: 'Select…' },
  { value: 'search', label: 'Search engine' },
  { value: 'social', label: 'Social media' },
  { value: 'referral', label: 'Friend or colleague' },
  { value: 'event', label: 'Conference / event' },
  { value: 'partner', label: 'Partner' },
  { value: 'other', label: 'Other' },
];

const DAY_OPTIONS = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

const emptyBlock = () => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  days: ['mon', 'tue', 'wed', 'thu', 'fri'],
  start: '09:00',
  end: '17:00',
});

const RegisterPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [showOptional, setShowOptional] = useState(false);

  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [industry, setIndustry] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [location, setLocation] = useState('');
  const [businessHoursText, setBusinessHoursText] = useState('');
  const [servicesDescription, setServicesDescription] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [scheduleBlocks, setScheduleBlocks] = useState([emptyBlock()]);

  const schedulePayload = useMemo(() => {
    if (!showOptional) return undefined;
    const blocks = scheduleBlocks
      .map((b) => ({
        days: b.days || [],
        start: (b.start || '').trim(),
        end: (b.end || '').trim(),
      }))
      .filter((b) => b.days.length > 0 && b.start && b.end);
    return blocks.length > 0 ? blocks : undefined;
  }, [showOptional, scheduleBlocks]);

  const updateBlock = useCallback((key, patch) => {
    setScheduleBlocks((prev) => prev.map((b) => (b.key === key ? { ...b, ...patch } : b)));
  }, []);

  const addBlock = useCallback(() => {
    setScheduleBlocks((prev) => [...prev, emptyBlock()]);
  }, []);

  const removeBlock = useCallback((key) => {
    setScheduleBlocks((prev) => (prev.length <= 1 ? prev : prev.filter((b) => b.key !== key)));
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) {
      setDone(true);
      return;
    }
    if (!contactFirstName.trim() || !contactLastName.trim()) {
      toast({ title: 'Please enter your name', status: 'warning' });
      return;
    }
    if (!contactEmail.trim() || !companyName.trim() || companyDescription.trim().length < 10) {
      toast({
        title: 'Required fields missing',
        description: 'Company name, email, and a short description (at least 10 characters) are required.',
        status: 'warning',
      });
      return;
    }

    setSubmitting(true);
    try {
      await PublicAPI.registerLead({
        contact_first_name: contactFirstName.trim(),
        contact_last_name: contactLastName.trim(),
        contact_email: contactEmail.trim(),
        contact_phone: contactPhone.trim() || undefined,
        company_name: companyName.trim(),
        company_phone: companyPhone.trim() || undefined,
        industry: industry || undefined,
        company_description: companyDescription.trim(),
        location: showOptional ? location.trim() || undefined : undefined,
        business_hours_text: showOptional ? businessHoursText.trim() || undefined : undefined,
        schedule_config: schedulePayload,
        services_description: showOptional ? servicesDescription.trim() || undefined : undefined,
        referral_source: showOptional && referralSource ? referralSource : undefined,
        website: honeypot || undefined,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast({
        title: 'Could not submit',
        description: extractApiError(err),
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Box bg="#F7FAFC" minH="100vh" py={{ base: 16, md: 24 }}>
        <Container maxW="lg">
          <MotionBox
            bg="white"
            p={{ base: 8, md: 12 }}
            rounded="xl"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="lg"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Heading size="lg" color="gray.900" mb={4}>
              Thank you
            </Heading>
            <Text color="gray.700" lineHeight={1.8} mb={6}>
              We received your information. Our team will review your request and reach out by email within 1–2 business days.
            </Text>
            <Button as={RouterLink} to="/" colorScheme="blue" rounded="full">
              Back to home
            </Button>
            <Button ml={3} variant="outline" rounded="full" onClick={() => navigate('/login')}>
              Sign in
            </Button>
          </MotionBox>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="#F7FAFC" minH="100vh" py={{ base: 8, md: 12 }}>
      <Container maxW="3xl">
        <Button
          leftIcon={<FiArrowLeft />}
          variant="ghost"
          mb={6}
          onClick={() => navigate('/')}
        >
          Back
        </Button>

        <MotionBox
          bg="white"
          p={{ base: 6, md: 10 }}
          rounded="xl"
          border="1px solid"
          borderColor="gray.200"
          boxShadow="md"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Heading size="xl" color="gray.900" mb={2}>
            Get started with Bizsprite
          </Heading>
          <Text color="gray.600" mb={8}>
            Tell us about your business. We use this to prepare your AI receptionist setup. No payment required on this step.
          </Text>

          <form onSubmit={onSubmit}>
            {/* Honeypot — hidden from users */}
            <Box position="absolute" left="-9999px" top="auto" w="1px" h="1px" overflow="hidden" aria-hidden="true">
              <label htmlFor="reg-website">Website</label>
              <input
                id="reg-website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </Box>

            <Stack spacing={8}>
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="blue.600" textTransform="uppercase" letterSpacing="wider" mb={4}>
                  Contact person
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>First name</FormLabel>
                    <Input value={contactFirstName} onChange={(e) => setContactFirstName(e.target.value)} />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Last name</FormLabel>
                    <Input value={contactLastName} onChange={(e) => setContactLastName(e.target.value)} />
                  </FormControl>
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                  <FormControl isRequired>
                    <FormLabel>Work email</FormLabel>
                    <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Phone</FormLabel>
                    <Input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Divider />

              <Box>
                <Text fontWeight="bold" fontSize="sm" color="blue.600" textTransform="uppercase" letterSpacing="wider" mb={4}>
                  Company
                </Text>
                <FormControl isRequired mb={4}>
                  <FormLabel>Company name</FormLabel>
                  <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Industry</FormLabel>
                  <Select value={industry} onChange={(e) => setIndustry(e.target.value)}>
                    {INDUSTRIES.map((o) => (
                      <option key={o.value || 'empty'} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Company description</FormLabel>
                  <FormHelperText mb={2}>What does your business do? (1–3 sentences)</FormHelperText>
                  <Textarea
                    rows={4}
                    value={companyDescription}
                    onChange={(e) => setCompanyDescription(e.target.value)}
                    placeholder="e.g. We provide residential HVAC repair and installation in Orange County."
                  />
                </FormControl>
                <FormControl mt={4}>
                  <FormLabel>Main business phone</FormLabel>
                  <Input type="tel" value={companyPhone} onChange={(e) => setCompanyPhone(e.target.value)} />
                </FormControl>
              </Box>

              <Button variant="link" colorScheme="blue" alignSelf="flex-start" onClick={() => setShowOptional((s) => !s)}>
                {showOptional ? '− Hide' : '+ Add'} business details (hours, location, services)
              </Button>

              <Collapse in={showOptional} animateOpacity>
                <Stack spacing={6} pt={2}>
                  <FormControl>
                    <FormLabel>Location / service area</FormLabel>
                    <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, state, or areas you serve" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Business hours (free text)</FormLabel>
                    <Textarea
                      rows={3}
                      value={businessHoursText}
                      onChange={(e) => setBusinessHoursText(e.target.value)}
                      placeholder="e.g. Mon–Fri 8am–5pm, Sat 9am–1pm, Sun closed"
                    />
                  </FormControl>
                  <Box>
                    <Text fontWeight="semibold" mb={2}>
                      Weekly schedule (optional)
                    </Text>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                      Add one or more blocks with days and open/close times.
                    </Text>
                    <VStack align="stretch" spacing={4}>
                      {scheduleBlocks.map((block) => (
                        <Box key={block.key} p={4} borderWidth="1px" borderRadius="md" borderColor="gray.200">
                          <CheckboxGroup
                            value={block.days}
                            onChange={(vals) => updateBlock(block.key, { days: vals })}
                          >
                            <HStack flexWrap="wrap" spacing={3}>
                              {DAY_OPTIONS.map((d) => (
                                <Checkbox key={d.value} value={d.value}>
                                  {d.label}
                                </Checkbox>
                              ))}
                            </HStack>
                          </CheckboxGroup>
                          <HStack mt={3} spacing={4}>
                            <FormControl maxW="140px">
                              <FormLabel fontSize="sm">Opens</FormLabel>
                              <Input
                                type="time"
                                value={block.start}
                                onChange={(e) => updateBlock(block.key, { start: e.target.value })}
                              />
                            </FormControl>
                            <FormControl maxW="140px">
                              <FormLabel fontSize="sm">Closes</FormLabel>
                              <Input
                                type="time"
                                value={block.end}
                                onChange={(e) => updateBlock(block.key, { end: e.target.value })}
                              />
                            </FormControl>
                            <Button
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              leftIcon={<FiTrash2 />}
                              alignSelf="flex-end"
                              onClick={() => removeBlock(block.key)}
                              isDisabled={scheduleBlocks.length <= 1}
                            >
                              Remove
                            </Button>
                          </HStack>
                        </Box>
                      ))}
                      <Button size="sm" leftIcon={<FiPlus />} variant="outline" onClick={addBlock}>
                        Add another schedule block
                      </Button>
                    </VStack>
                  </Box>
                  <FormControl>
                    <FormLabel>Services you offer</FormLabel>
                    <Textarea
                      rows={4}
                      value={servicesDescription}
                      onChange={(e) => setServicesDescription(e.target.value)}
                      placeholder="List main services or appointment types customers ask about."
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>How did you hear about us?</FormLabel>
                    <Select value={referralSource} onChange={(e) => setReferralSource(e.target.value)}>
                      {REFERRALS.map((o) => (
                        <option key={o.value || 'r-empty'} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Collapse>

              <Text fontSize="sm" color="gray.500">
                By submitting, you agree to our{' '}
                <Link as={RouterLink} to="/terms" color="blue.500">
                  Terms
                </Link>{' '}
                and{' '}
                <Link as={RouterLink} to="/privacy-policy" color="blue.500">
                  Privacy Policy
                </Link>
                .
              </Text>

              <Button type="submit" colorScheme="blue" size="lg" rounded="full" isLoading={submitting} loadingText="Submitting">
                Submit
              </Button>
            </Stack>
          </form>
        </MotionBox>

        <Flex justify="center" mt={8} color="gray.600" fontSize="sm">
          <Text>
            Already have an account?{' '}
            <Link as={RouterLink} to="/login" color="blue.500" fontWeight="semibold">
              Sign in
            </Link>
          </Text>
        </Flex>
      </Container>
    </Box>
  );
};

export default RegisterPage;
